import { useQuery } from '@apollo/client';
import { faCloudDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as R from 'ramda';
import { FormProvider, useForm } from 'react-hook-form';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import { Panel } from '~/components/common/Panel';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/forms/Checkbox';
import { ReactSelectInput } from '~/components/ui/forms/react-select-input';
import { saveAs, timestamp } from '~/utils/export';
import { createKmlDocument, createKmlFolder } from '~/utils/kml-v4';

const REGION_LIST = graphql(`
  query ExportGeoreferencesRegion_RegionList {
    regionList(first: 9999) {
      results {
        id
        name
      }
    }
  }
`);

export default function ExportGeoreferencesRegionPage() {
  return (
    <ApolloProviderV4>
      <ExportGeoreferencesRegionPageInner />
    </ApolloProviderV4>
  );
}

export function ExportGeoreferencesRegionPageInner() {
  const { data, loading } = useQuery(REGION_LIST);

  const regionList = R.sortBy(
    region => region.name,
    data?.regionList?.results ?? [],
  );

  const form = useForm({
    values: {
      regionId: '',
      includeOutcrops: true,
    },
  });

  if (loading) {
    return <SpinnerPlaceholder />;
  }

  const regionId = ~~form.watch('regionId');
  const includeOutcrops = form.watch('includeOutcrops');

  return (
    <Panel>
      <Panel.Heading>
        <Panel.Title>Export Georeferences by Region</Panel.Title>
      </Panel.Heading>

      <Panel.Body>
        <FormProvider {...form}>
          <form className="space-y-2">
            <ReactSelectInput
              name="regionId"
              options={regionList.map(region => ({
                value: region.id,
                label: region.name,
              }))}
            />

            <Checkbox
              {...form.register('includeOutcrops')}
              label="Include outcrop and study data"
            />
          </form>
        </FormProvider>
      </Panel.Body>

      <Panel.Footer align="right">
        {regionId ? (
          <DownloadButton
            regionId={regionId}
            includeOutcrops={includeOutcrops}
          />
        ) : (
          <Button type="button" color="primary" loading={loading} disabled>
            Export
          </Button>
        )}
      </Panel.Footer>
    </Panel>
  );
}

const REGION_GEOREFERENCES = graphql(`
  query ExportGeoreferencesRegion_RegionGeoreferences($id: ID!) {
    regionGet(id: $id) {
      id
      name
      georeferences {
        ...georeferenceParts
      }
      outcrops {
        id
        name
        georeferences {
          ...georeferenceParts
        }
        studies {
          id
          name
          georeferences {
            ...georeferenceParts
          }
        }
      }
    }
  }
`);

function DownloadButton({
  regionId,
  includeOutcrops,
}: {
  regionId: number;
  includeOutcrops: boolean;
}) {
  const { data, loading } = useQuery(REGION_GEOREFERENCES, {
    variables: { id: regionId },
  });

  async function handleDownload() {
    const region = data?.regionGet;
    if (!region) {
      console.error('No region data found');
      return;
    }

    let regionOutcropsFolders: any = null;
    if (includeOutcrops) {
      const outcropFolders = region.outcrops.map(outcrop => {
        return createKmlFolder(
          outcrop.name,
          `${outcrop.name} Georeferences`,
          outcrop.georeferences,
        );
      });
      regionOutcropsFolders = [
        createKmlFolder('Outcrops', null, [], outcropFolders),
      ];
    }

    const regionFolder = createKmlFolder(
      region.name,
      `${region.name} Georeferences`,
      region.georeferences,
      regionOutcropsFolders,
    );

    const document = await createKmlDocument([regionFolder]);
    const filename = `${region.name} Georeferences Export ${timestamp()}.kml`;
    saveAs(document, filename, 'application/vnd;charset=utf-8');
  }

  return (
    <Button
      type="button"
      color="primary"
      loading={loading}
      onClick={handleDownload}
      startIcon={<FontAwesomeIcon icon={faCloudDownload} />}
    >
      Download KML
    </Button>
  );
}
